import { normalizeText } from '../utils/textNormalize';

const STORAGE_PREFIX = 'lexio:wordbanks';
const MANIFEST_STORAGE_KEY = `${STORAGE_PREFIX}:manifest`;
const BANK_STORAGE_KEY = (bankId) => `${STORAGE_PREFIX}:bank:${bankId}`;
const BASE_PATH = process.env.PUBLIC_URL || '';
const MANIFEST_URL = `${BASE_PATH}/wordbanks/manifest.json`;

let workerInstance = null;
let offlineInitPromise = null;
let offlineReady = false;
let offlineEntryCount = 0;
let messageId = 0;
const pendingMessages = new Map();

const isBrowser = () => typeof window !== 'undefined';

const getStorage = () => {
  if (!isBrowser()) return null;
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const readCache = (key) => {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const value = storage.getItem(key);
    if (!value) return null;
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const writeCache = (key, value) => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore quota errors.
  }
};

const resolveBankUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BASE_PATH}${path}`;
};

const fetchJson = async (url) => {
  const response = await fetch(url, { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }
  return response.json();
};

const loadManifest = async () => {
  try {
    const manifest = await fetchJson(MANIFEST_URL);
    writeCache(MANIFEST_STORAGE_KEY, manifest);
    return manifest;
  } catch (error) {
    const cached = readCache(MANIFEST_STORAGE_KEY);
    if (cached) return cached;
    throw error;
  }
};

const loadBankEntries = async (bank) => {
  const cacheKey = BANK_STORAGE_KEY(bank.id);
  const cached = readCache(cacheKey);
  if (cached && cached.version === bank.version) {
    return cached.entries;
  }

  const url = resolveBankUrl(bank.path);
  if (!url) return [];

  try {
    const data = await fetchJson(url);
    const entries = Array.isArray(data) ? data : data.entries;
    const preparedEntries = entries.map((entry) => ({ ...entry, bankId: bank.id }));
    writeCache(cacheKey, { version: bank.version, entries: preparedEntries });
    return preparedEntries;
  } catch (error) {
    if (cached) return cached.entries;
    throw error;
  }
};

const normalizeEntry = (entry, index) => {
  const slug = entry.slug || entry.word?.toLowerCase().replace(/\s+/g, '-');
  const frequencyRank =
    typeof entry.frequencyRank === 'number'
      ? entry.frequencyRank
      : typeof entry.frequency === 'number'
        ? entry.frequency
        : null;
  const normalizedValue = entry.normalized
    ? normalizeText(entry.normalized)
    : normalizeText(entry.word || slug || '');
  return {
    id: entry.id || `${entry.bankId || 'bank'}-${index}`,
    word: entry.word,
    slug,
    lang: entry.lang || 'en',
    kind: entry.kind || 'word',
    bankId: entry.bankId,
    aliases: entry.aliases || [],
    phonetic: entry.phonetic || [],
    preview: entry.preview || '',
    tags: entry.tags || [],
    frequencyRank,
    normalized: normalizedValue,
  };
};

const ensureWorker = () => {
  if (!isBrowser()) {
    throw new Error('Offline search is only available in the browser.');
  }
  if (workerInstance) return workerInstance;
  workerInstance = new Worker(new URL('../workers/offlineSearchWorker.js', import.meta.url));
  workerInstance.onmessage = (event) => {
    const { id, type, payload } = event.data;
    if (!id) return;
    const pending = pendingMessages.get(id);
    if (!pending) return;
    pendingMessages.delete(id);
    if (pending.expectedType && pending.expectedType !== type) {
      pending.reject(new Error(`Unexpected worker response: ${type}`));
      return;
    }
    pending.resolve(payload);
  };
  workerInstance.onerror = (error) => {
    pendingMessages.forEach(({ reject }) => reject(error));
    pendingMessages.clear();
    workerInstance.terminate();
    workerInstance = null;
    offlineInitPromise = null;
    offlineReady = false;
  };
  return workerInstance;
};

const callWorker = (type, payload, expectedType) => {
  const worker = ensureWorker();
  const id = `${Date.now()}-${messageId++}`;
  const messagePayload = payload || {};
  return new Promise((resolve, reject) => {
    pendingMessages.set(id, { resolve, reject, expectedType });
    worker.postMessage({ id, type, payload: messagePayload });
    setTimeout(() => {
      if (pendingMessages.has(id)) {
        pendingMessages.delete(id);
        reject(new Error(`Offline search worker timeout for ${type}`));
      }
    }, 10000);
  });
};

const buildIndex = async () => {
  const manifest = await loadManifest();
  const bankEntries = await Promise.all(manifest.banks.map((bank) => loadBankEntries(bank)));
  const entries = bankEntries.flat().map((entry, index) => normalizeEntry(entry, index));
  offlineEntryCount = entries.length;
  await callWorker('BUILD_INDEX', { entries, manifestVersion: manifest.version }, 'INDEX_READY');
  return { manifestVersion: manifest.version, entriesLoaded: entries.length };
};

export const initOfflineSearch = async () => {
  if (offlineReady) {
    return { manifestVersion: null, entriesLoaded: offlineEntryCount };
  }
  if (offlineInitPromise) return offlineInitPromise;
  offlineInitPromise = buildIndex()
    .then((result) => {
      offlineReady = true;
      return result;
    })
    .catch((error) => {
      offlineReady = false;
      offlineInitPromise = null;
      throw error;
    });
  return offlineInitPromise;
};

export const isOfflineSearchReady = () => offlineReady;

export const searchOffline = async (query, options = {}) => {
  if (!query || !query.trim()) return [];
  if (!offlineReady) {
    await initOfflineSearch().catch(() => null);
  }
  if (!offlineReady) return [];
  const payload = {
    query,
    limit: options.limit || 20,
  };
  try {
    const result = await callWorker('SEARCH', payload, 'SEARCH_RESULT');
    return result.results || [];
  } catch (error) {
    return [];
  }
};

export const getOfflineSearchStats = () => ({
  ready: offlineReady,
  entryCount: offlineEntryCount,
});
